<template>
  <div :class="toolbarClasses" v-if="showSharingDetails"> 
    <div class="toolbar-expanded-items" v-bind:style="toolbarStyle"> 
      <div class="map-attributes">
        <div :class="logoClasses">
          <a :href="config.homepage" target="_blank"><img :src="`${clientResourcesUrl}logo.png?alt=media`" alt="App Logo" class="app-logo"></a>
        </div>
        <div class="attrs">
          <h1 class="attr-header">Project Description</h1>
          <p class="attr-description">{{ projectName }}</p>
        </div>
        <div class="attrs address">
          <h1 class="attr-header">Address</h1>
          <p class="attr-description">{{ formattedAddress }}</p>
        </div>
      </div>
      <div class="toolbar-controls show">
        <a href="#" title="Cancel editing" @click.prevent="cancelEditing">
          <span>cancel</span>
          <span class="icon">
            <cancel-icon />
          </span>
        </a>
        <a href="#" title="Save changes" @click.prevent="saveMap">
          <span>save</span>
          <span class="icon">
            <content-save-icon />
          </span>
        </a>
      </div>      
    </div>
  </div>
</template>

<style scoped>
  .toolbar-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    box-shadow: 0px 3px 6px var(--app-toolbar-shadow);
    padding: .8em;
    background-color: var(--app-toolbar-bg);
    color: var(--app-text);
    display: flex;
    flex-direction: row;
  }

  .toolbar-container.collapsed {
    height: 0;
    overflow: hidden;
  }

  div.toolbar-expanded-items {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex-grow: 1;
  }

  div.toolbar-container.xs div.toolbar-expanded-items, div.toolbar-container.xs div.toolbar-expanded-items div.map-attributes {
    flex-direction: column;
    align-items: flex-start;
  }

  div.map-attributes {
    display: flex;
  }

  .toolbar-container.collapsed {
    height: 0;
    overflow: hidden;
  }

  .toolbar-container div.attrs {
    border-left: 1px solid var(--app-border-light);
    padding: 0 .5em;
  }

  .app-logo {
    width: 100px;
    padding-right: 1em;
  }

  div.level-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

  }

  div.attrs > * {
    display: block;
  }

  div.attrs.address {
    flex-wrap: wrap;
    flex-shrink: 3;
    flex-grow: 0;
    word-wrap: break-word;
  }

  div.attrs h1 {
    font-size: .8em;
    font-weight: bold;
  }

  div.results {
    display: flex;
    flex-shrink: 0;
    flex-direction: row;
    justify-content: flex-start;
  }

  .toolbar-controls, .visibility-toolbar {
    display: flex;
    flex-direction: row;
    
    /* align-self: flex-end; */
    justify-content: flex-end;
    flex-wrap: wrap;
    flex-shrink: 1;
    flex-grow: 3;
  }

  .toolbar-controls a, .visibility-toolbar a {
    display: block;
    border-radius: 10px;
    padding: 2px 10px;
    margin: 2px;
    border: 1px solid var(--app-border-light);
    font-size: .8em;
    background-color: var(--app-light-gray);
    min-width: 100px;
    text-align: center;
  }

  div.toolbar-controls a:hover, div.visibility-toolbar a:hover {
    box-shadow: 0 0 3px #000;
  }

  .toolbar-controls a {
    color: var(--app-text);
  }  

  div.toolbar-container.collapsed {
    background-color: transparent;
  }

  div.toolbar-container.xs div.toolbar-controls, div.visibility-toolbar.hide {
    position: absolute;
    top: 5px;
    right: 5px;
  }

  div.toolbar-container.collapsed div.toolbar-controls.hide a, div.visibility-toolbar.hide a {
    border-radius: 0 0 10px 10px;
    border-bottom: 1px solid var(--app-border-light);
  }

  /* add a hover style to toolbar-container */
  div.toolbar-controls a:hover {
  }

  .visibility-toolbar {
    position: absolute;
    right: 0;
    bottom: 0;
    border-radius: 10px 10px 0 0;
  }

  div.visibility-toolbar:hover {
  }

  div.visibility-toolbar:hover a {
    color: #fff;
  }

  .visibility-toolbar a {
    color: #000;
  }  
  
</style>

<script>
import { computed, getCurrentInstance } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useMainStore } from '@/stores/main'
import { useMapSharingStore } from '@/stores/mapSharing'
import { useDialogsStore } from '@/stores/dialogs'
import { useConfig } from '@/composables/useConfig'

export default {
  setup() {
    const router = useRouter()
    const route = useRoute()
    const mainStore = useMainStore()
    const mapSharingStore = useMapSharingStore()
    const dialogsStore = useDialogsStore()
    const instance = getCurrentInstance()
    const { config, clientResourcesUrl } = useConfig()

    const hide = computed(() => mainStore.mapDrag || mainStore.toolbarsHidden)

    const showSharingDetails = computed(() => {
      return route.meta.showSharingDetails ? true : false
    })

    const toolbarStyle = computed(() => ({
      opacity: hide.value ? 0 : 1,
      visibility: hide.value ? 'hidden' : 'visible',
      transition: 'opacity 0.5s ease, visibility 0.5s ease'
    }))

    const toolbarClasses = computed(() => {
      const classes = ['toolbar-container', 'level', instance.proxy.$mq]
      if (hide.value) {
        classes.push('collapsed')
      }
      return classes
    })

    const logoClasses = computed(() => {
      const classes = ['logo-container']
      if (instance.proxy.$mq === 'xs') {
        classes.push(instance.proxy.$mq)
        return classes
      }

      classes.push('level-item')
      return classes
    })

    const totalAreaString = computed(() => {
      return mainStore.totalShapeCalculatedConverted
        ? `${mainStore.totalShapeCalculatedConverted.toLocaleString()} ${mainStore.measureSuffix}`
        : '---'
    })

    const projectName = computed(() => {
      let name = mainStore.projectName || 'Measurement Results'
      if (name.length > 20 && instance.proxy.$mq == 'sm') {
        name = name.substring(0, 30) + '...'
      }
      return name
    })

    const formattedAddress = computed(() => {
      return mainStore.displayAddress || mainStore.address || '---'
    })

    const toggleToolbarVisibility = () => {
      mainStore.setToolbarsHidden(!mainStore.toolbarsHidden)
    }

    const cancelEditing = () => {
      mainStore.initializeStore({ mapId: mapSharingStore.mapId })
      router.replace({ name: 'MapResults' })
    }

    const saveMap = async () => {
      try {
        mainStore.setLoading(true)
        await mapSharingStore.updateSharedMap()
        router.push({ name: 'MapResults' })

        dialogsStore.snackbar('Your measurements have been saved.')
      } catch (error) {
        dialogsStore.alert(0, `Could not save your measurements. ${error.message}`, {
          title: 'Error',
          type: 'is-error'
        })

        throw error
      } finally {
        mainStore.setLoading(false)
      }
    }

    const editMap = () => {
      router.push({ name: 'MapResultsEdit' })
    }

    return {
      hide,
      showSharingDetails,
      toolbarStyle,
      toolbarClasses,
      logoClasses,
      totalAreaString,
      projectName,
      formattedAddress,
      config,
      clientResourcesUrl,
      mapId: computed(() => mapSharingStore.mapId),
      toggleToolbarVisibility,
      cancelEditing,
      saveMap,
      editMap
    }
  }
}
</script>
